import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import template from './ns-shipping-info.html';
import {SHIPPING_INFO_CONFIG} from './shipping-info-config';
import {UserService} from '@nuskin/ns-account';
import {ConfigService} from '@nuskin/ns-util';
import STATE_CONFIG from '../temp/state-config';
import {SHIPPING_ADDRESS_CONFIG} from '@nuskin/ns-form-elements/src/ns-shipping-address/shipping-address-config.js'

const BRAND_AFFILIATE = '10'

/**
 * This component is used for the Shipping Information as a part ADR Manage Shipping Address.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Shipping Info Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-shipping-info
 *       shipping-address1="504 S Sharp St"
 *       shipping-city="Baltimore"
 *       shipping-state="MD"
 *       shipping-postal-code="21201">
 *     </ns-shipping-info>
 *   </template>
 * </demo-snippet>
 */
export class NsShippingInfo extends PolymerElement {
    static get is() {
        return 'ns-shipping-info';
    }

    /**
     * Sets the html template
     */
    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    /**
     * Sets properties to pass to this component
     */
    static get properties() {
        return {
            /**
             * Shipping Address Id
             */
            beShippingAddressId: {
                type: String
            },
            /**
             * Display Order
             * String Options are: 'city', 'state', 'zip', 'county', 'country'
             * Default: [['city', 'state', 'zip']] - This option will display: Provo, UT 84601 (county and country will not be displayed.)
             * Other examples: [['county', 'zip', 'country], ['state', 'city']] will display:
             *      Utah County, 84601 US
             *      Utah Provo
             * At this time, the comma punctuation is always after the first field.  Will be re-worked once the need arise for a different placement
             */
            displayOrder: {
                type: Array,
                value: [
                    ['address1'],
                    ['address2'],
                    ['city', 'state', 'zip']
                ]
            },
            /**
             * Shipping Alias assigned
             */
            shippingAlias: {
                type: String
            },
            /**
             * Name of person or company for that shipping address
             */
            shippingAddressName: {
                type: String
            },
            /**
             * Shipping Address Line 1
             */
            shippingAddress1: {
                type: String,
                observer: '_shippingAddress1Changed'
            },
            /**
             * Shipping Address Line 2
             */
            shippingAddress2: {
                type: String,
                observer: '_shippingAddress2Changed'
            },
            /**
             * Shipping City
             */
            shippingCity: {
                type: String,
                observer: '_shippingCityChanged'
            },
            /**
             * Shipping State
             */
            shippingState: {
                type: String,
                observer: '_shippingStateChanged'
            },
            /**
             * Shipping County
             */
            shippingCounty: {
                type: String,
                observer: '_shippingCountyChanged'
            },
            /**
             * Shipping Zip Code
             */
            shippingPostalCode: {
                type: String,
                observer: '_shippingZipChanged'
            },
            /**
             * Shipping Country
             */
            shippingCountry: {
                type: String
            },
            /**
             * Shipping Phone
             */
            shippingPhone: {
                type: String
            },
            /**
             * Shipping Mobile Phone
             */
            shippingMobilePhone: {
                type: String
            },
            /**
             * Phone label for shipping form
             */
            shippingPhoneLabel: {
                type: String
            },
            /**
             * Mobile phone label for shipping form
             */
            shippingMobilePhoneLabel: {
                type: String
            },
            /**
             * Text for shipping mobile phone
             */
            shippingMobilePhoneText: {
                type: String
            },
            allowMobilePhone: {
                type: Boolean,
                value: false
            },
            showMobilePhone: {
                type: Boolean,
                value: false
            },
            /**
             * Fields for configuring the ship from name
             */
            showShipFromName: {
                type: Boolean,
                value: false
            },
            shippingShipFromNameLabel: {
                type: String
            },
            shippingShipFromName: {
                type: String
            }
        };
    }

    static get observers() {
        return [
            '_computeShowMobilePhone(shippingMobilePhoneText, allowMobilePhone)'
        ]
    }

    ready() {
        super.ready();
        this._initialize();
    }

    /**
     * Sets up config if found, otherwise, default values will apply.
     */
    _initialize() {
        let country = this.shippingCountry || ConfigService.getMarketConfig() || 'US';
        let config = SHIPPING_INFO_CONFIG[country];
        if (config) {
            Object.entries(config).forEach(([key, value]) => {
                this[key] = value;
            });
        }
        const user = UserService.getUser();
        const isBrandAffiliate = user && user.accountType === BRAND_AFFILIATE;
        const adpConfig = ConfigService.getMarketConfig().adpManage;
        const onlyShowForBrandAffiliates = !!adpConfig.showShipFromNameOnlyForBrandAffiliates;
        let showShipFromName = !!adpConfig.showShipFromName;
        if (onlyShowForBrandAffiliates && !isBrandAffiliate) {
            showShipFromName = false;
        }

        this.allowMobilePhone = !!(SHIPPING_ADDRESS_CONFIG[country] || {}).showMobilePhoneField;
        this.showShipFromName = showShipFromName;
    }

    /**
     * Watches dom change/template stamping of dom-repeat.
     * Also sets property values if both element and value exists/changes
     * @param {event} e
     */
    _domChange(e) {
        if (this.shadowRoot.querySelector('#country') && this.shippingCountry) this.shadowRoot.querySelector('#country').textContent = this.shippingCountry;
        this._shippingAddress1Changed();
        this._shippingAddress2Changed();
        this._shippingCityChanged();
        this._shippingStateChanged();
        this._shippingZipChanged();
        this._shippingCountyChanged();
    }

    /**
     * shippingAddress1 Observer.
     * Sets textContent of element to the shippingCity value.
     */
    _shippingAddress1Changed() {
        if (this.shadowRoot.querySelector('#address1')) {
            if (this.shippingAddress1) {
                this.shadowRoot.querySelector('#address1').classList.remove('hidden');
                this.shadowRoot.querySelector('#address1').textContent = this.shippingAddress1;
            } else {
                this.shadowRoot.querySelector('#address1').classList.add('hidden');
            }
        }
    }

    /**
     * shippingCity Observer.
     * Sets textContent of element to the shippingCity value.
     */
    _shippingAddress2Changed() {
        if (this.shadowRoot.querySelector('#address2')) {
            if (this.shippingAddress2) {
                this.shadowRoot.querySelector('#address2').classList.remove('hidden');
                this.shadowRoot.querySelector('#address2').textContent = this.shippingAddress2;
            } else {
                this.shadowRoot.querySelector('#address2').classList.add('hidden');
            }
        }
    }

    /**
     * shippingCity Observer.
     * Sets textContent of element to the shippingCity value.
     */
    _shippingCityChanged() {
        if (this.shadowRoot.querySelector('#city') && this.shippingCity) {
            this.shadowRoot.querySelector('#city').textContent = this.shippingCity;
            if (nuskin.configuration.checkout.shippingAddressFormat !== "PostalCodeStateCity") {
                this.shadowRoot.querySelector('#city').classList.add('insert-comma');
            }
        }
    }

    /**
     * shippingState Observer
     * Sets textContent of element to the shippingState value
     */
    _shippingStateChanged() {
        const stateList = STATE_CONFIG[this.shippingCountry] ? STATE_CONFIG[this.shippingCountry].stateList : [];
        let stateObject = stateList.find(item => item.key === this.shippingState);
        let stateValue = stateObject && stateObject.value ? stateObject.value : this.shippingState;
        if (this.shadowRoot.querySelector('#state')) this.shadowRoot.querySelector('#state').textContent = stateValue;
    }

    /**
     * shippingCounty Observer
     * Sets textContent of element to the shippingCounty value
     */
    _shippingZipChanged() {
        if (this.shadowRoot.querySelector('#zip') && this.shippingPostalCode) this.shadowRoot.querySelector('#zip').textContent = this.shippingPostalCode;
    }

    /**
     * shippingCounty Observer
     * Sets textContent of element to the shippingCounty value
     */
    _shippingCountyChanged() {
        if (this.shadowRoot.querySelector('#county') && this.shippingCounty) this.shadowRoot.querySelector('#county').textContent = this.shippingCounty;
    }

    _computeShowMobilePhone(phoneText, allowPhone) {
        this.showMobilePhone = phoneText && allowPhone;
    }
}

customElements.define(NsShippingInfo.is, NsShippingInfo);
